import { SparkleIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';

import { formatAlertsToReview } from '../../AlertsCenter';

interface IFloatingAssistantPillBaseProps {
  /** Active alerts to review. Zero renders the neutral "Open Mantle Assistant" pill. */
  alertCount: number;
  onOpen: () => void;
}

/**
 * The assistant's entry point on a phone page: a pill fixed at the bottom
 * center of the viewport, 16px above its edge plus the safe-area inset (Figma
 * "Assistant CTA" in `9155:233665` and `9155:236135`). With alerts to review it
 * is brand-solid and states the count; with none it is a translucent neutral
 * surface reading "Open Mantle Assistant". Purely presentational — the wrapper
 * resolves the count and what a tap opens.
 */
const FloatingAssistantPillBase = ({ alertCount, onOpen }: IFloatingAssistantPillBaseProps) => {
  const hasAlerts = alertCount > 0;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(16px+env(safe-area-inset-bottom))] z-40 flex justify-center px-4">
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={onOpen}
        className={cx(
          'pointer-events-auto flex h-11 min-w-0 max-w-full cursor-pointer items-center gap-1 rounded-full pl-3 pr-5',
          'outline-focus-ring transition-colors duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2',
          hasAlerts
            ? 'bg-brand-solid hover:bg-brand-solid_hover shadow-[0_0_2px_rgba(0,0,0,0.06),0_4px_14px_rgba(0,0,0,0.08)]'
            : // An inset ring rather than a border: Figma's inside stroke takes no room from the padding.
              'bg-primary/80 hover:bg-primary ring-(--color-bg-primary) shadow-[0_0_4px_rgba(0,0,0,0.06),0_4px_28px_rgba(0,0,0,0.08)] ring-1 ring-inset'
        )}
      >
        {/* Figma insets a 17px glyph in a 20px slot. */}
        <span className="flex size-5 shrink-0 items-center justify-center">
          <SparkleIcon aria-hidden="true" size={17} className={hasAlerts ? 'text-white' : 'text-utility-brand-400'} />
        </span>
        <span className={cx('text-md min-w-0 truncate font-medium', hasAlerts ? 'text-white' : 'text-secondary')}>
          {hasAlerts ? formatAlertsToReview(alertCount) : 'Open Mantle Assistant'}
        </span>
      </button>
    </div>
  );
};

export default FloatingAssistantPillBase;
